import React from "react";
import MaterialIcon from "../MaterialIcon";
import ChatAvatar from "./ChatAvatar";
import "./chat-pro.css";

// Small pieces of the mobile Pro chat thread (v2/pro/proChatKit.js, ChatComposerPro.js):
// day chip, "N unread messages" band, system note, typing row, scroll-to-latest button
// and the select-mode check.

/** Day separator: 26px pill, white with a 1px line, 12px semibold muted. `sticky` floats it under the header. */
export function DayChip({ label, sticky = false }) {
  if (!label) return null;
  return (
    <div className={`kjc-day${sticky ? " kjc-day--sticky" : ""}`} aria-hidden={sticky ? true : undefined}>
      <span className="kjc-day__chip">{label}</span>
    </div>
  );
}

/** "6 unread messages": full-width accent-soft band above the oldest unread message. */
export function UnreadBand({ label }) {
  return (
    <div className="kjc-unread" role="separator">
      <MaterialIcon icon="keyboard_double_arrow_down" size={16} weight={400} />
      <span>{label}</span>
    </div>
  );
}

/** System note: chip pill, 12px muted, centred, at most 85% wide. */
export function SystemNote({ children, focused = false }) {
  return (
    <div className="kjc-note">
      <div className={`kjc-note__chip${focused ? " is-focused" : ""}`}>{children}</div>
    </div>
  );
}

/** The typing bubble at the end of the thread (groups show the typist's avatar). */
export function TypingRow({ name, avatarSrc, showAvatar = false, label }) {
  return (
    <div className="kjc-typing" aria-label={label} title={label}>
      {showAvatar ? <ChatAvatar size={30} name={name} src={avatarSrc} /> : null}
      <div className="kjc-typing__bubble">
        <span className="kjc-typing__dot" />
        <span className="kjc-typing__dot" />
        <span className="kjc-typing__dot" />
      </div>
    </div>
  );
}

/** Scroll-to-latest: 44px round button with the orange new-message counter. */
export function ScrollToLatestButton({ visible, count = 0, onClick, label }) {
  if (!visible) return null;
  const n = Number(count) || 0;
  return (
    // kj-btn: custom.css squashes every other <button> to 38px high with !important.
    <button type="button" className="kj-btn kj-btn--icon kjc-fab" onClick={onClick} aria-label={label} title={label}>
      <MaterialIcon icon="keyboard_arrow_down" size={26} weight={400} />
      {n > 0 ? <span className="kjc-fab__count">{n > 99 ? "99+" : String(n)}</span> : null}
    </button>
  );
}

/** 22px round check of select mode (disabled rows show it dimmed and inert). */
export function SelectCheck({ on = false, disabled = false, onToggle, label }) {
  return (
    <button
      type="button"
      className={`kjc-check${on ? " is-on" : ""}${disabled ? " is-disabled" : ""}`}
      role="checkbox"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled && onToggle) onToggle();
      }}
    >
      {on ? <MaterialIcon icon="check" size={14} weight={700} /> : null}
    </button>
  );
}
